import { EnvironmentScope, targetParams, useClientTarget } from '@features/clients';
import { cn } from '@lib/cn';
import { Card, EmptyState, IconSearch, PageHeader, SelectField, StatTile } from '@shared/ui';
import { useState } from 'react';
import { Link, useNavigate } from 'react-router';

import { useActivationMuseums } from '../hooks/use-activation-tracking';
import {
    countMuseums,
    filterMuseums,
    museumCountLabel,
    neverOpenedHint,
    sortMuseums,
    toMuseumRow,
    type MuseumRowView,
    type OriginFilter,
    type KindFilter,
    type StatusFilter,
} from '../model/museum-list';

import { Td, Th } from './activation-table';
import { MuseumStatusBadge } from './MuseumStatusBadge';
import { ActivationPageFrame } from './page-frame';

import type { ReactNode } from 'react';

const STATUS_OPTIONS: readonly { value: StatusFilter; label: string }[] = [
    { value: 'all', label: 'All statuses' },
    { value: 'not_started', label: 'Not started' },
    { value: 'in_progress', label: 'In progress' },
    { value: 'completed', label: 'Completed' },
];

const ORIGIN_OPTIONS: readonly { value: OriginFilter; label: string }[] = [
    { value: 'all', label: 'All origins' },
    { value: 'invite', label: 'From invitation' },
    { value: 'tab', label: 'Came in directly' },
];

/**
 * Sandboxes are hidden by default, and that default is the point: with the
 * platform default on, the universe is the whole client catalogue, where scratch
 * orgs and E2E fixtures outnumber the museums. An operator opening this screen
 * is after museums.
 */
const KIND_OPTIONS: readonly { value: KindFilter; label: string }[] = [
    { value: 'real', label: 'Museums only' },
    { value: 'all', label: 'Include sandboxes' },
];

/**
 * Every museum with Feature activation turned on, and how far each one got.
 *
 * ## What the statuses are, and are not
 *
 * They are what the page saw on the museum's **last visit** — the hub only
 * learns anything when someone at the museum opens it. Hence the "as of" under
 * every status: a museum that finished its setup in Salesforce yesterday and
 * has not opened the page since still reads "In progress" here, and the date
 * is what tells an operator that.
 *
 * ## Who is on this screen
 *
 * Every museum the activation flag resolves ON for — its own override when it
 * has one, the platform default when it does not. Until 2026-10-05 it was the
 * overrides alone, which emptied the screen the day production switched the
 * default on. See `activation-flag.ts` in the API.
 *
 * ## The tiles follow the sandbox filter, and only that one
 *
 * They answer "how is the whole rollout going", which is the same question
 * whatever status or origin the table below is narrowed to. Scratch orgs are
 * the exception: a rollout total that counts E2E fixtures is not a number
 * anyone means, so hiding them hides them from the tiles too.
 */
export function ActivationListPage() {
    const targetState = useClientTarget();
    const { target, isSlotMissing } = targetState;
    const museums = useActivationMuseums(target);

    const [status, setStatus] = useState<StatusFilter>('all');
    const [origin, setOrigin] = useState<OriginFilter>('all');
    const [kind, setKind] = useState<KindFilter>('real');
    const [search, setSearch] = useState('');

    const all = museums.data ?? [];
    // The tiles count what the sandbox filter leaves, unlike the other filters:
    // a rollout total that includes scratch orgs is not a number anyone means.
    const counts = countMuseums(
        filterMuseums(all, { status: 'all', origin: 'all', kind, search: '' }),
    );
    const rows = sortMuseums(filterMuseums(all, { status, origin, kind, search })).map((museum) =>
        toMuseumRow(museum),
    );
    const linkSearch = new URLSearchParams(targetParams(target)).toString();

    // A number only once there is an answer: "0" while loading, or after a
    // failure, would be a claim about the rollout.
    const tile = (value: number) => (museums.data ? String(value) : '—');

    return (
        <ActivationPageFrame>
            <PageHeader
                eyebrow="Tenants"
                title="Feature Activation"
                description="How each museum is doing with the setup page in its Salesforce. Statuses come from the museum's last visit to that page, not from today."
            />

            <EnvironmentScope state={targetState} />

            <div className="mb-6 grid grid-cols-[repeat(auto-fit,minmax(180px,1fr))] gap-4">
                <StatTile label="Museums with the feature" value={tile(counts.total)} />
                <StatTile
                    label="Not started"
                    value={tile(counts.notStarted)}
                    hint={
                        museums.data ? (
                            // Regular weight, as the design: the kit's hint is
                            // semibold, and a node lets this one opt out.
                            <span className="font-normal">
                                {neverOpenedHint(counts.neverOpened)}
                            </span>
                        ) : undefined
                    }
                />
                <StatTile label="In progress" value={tile(counts.inProgress)} />
                <StatTile label="Completed" value={tile(counts.completed)} />
            </div>

            {/* The filters live inside the card, like the clients search: they
                narrow this table and nothing else, so they belong to its frame. */}
            <Card>
                <div className="flex flex-wrap items-center gap-3 border-b border-line-subtle px-[18px] py-3.5">
                    <p className="text-[13px] font-semibold text-content-subtle">
                        {museums.data
                            ? museumCountLabel(rows.length)
                            : museums.isPending && !isSlotMissing
                              ? 'Loading…'
                              : museumCountLabel(0)}
                    </p>

                    {/* `SelectField` brings a stacked label sized for a form
                        column; the design has none. The label stays for screen
                        readers (`sr-only`) and only the furniture goes. */}
                    <div className="ml-auto w-[180px] [&_label]:sr-only">
                        <SelectField
                            label="Client kind"
                            value={kind}
                            onChange={setKind}
                            options={KIND_OPTIONS}
                        />
                    </div>
                    <div className="w-[170px] [&_label]:sr-only">
                        <SelectField
                            label="Status"
                            value={status}
                            onChange={setStatus}
                            options={STATUS_OPTIONS}
                        />
                    </div>
                    <div className="w-[170px] [&_label]:sr-only">
                        <SelectField
                            label="Origin"
                            value={origin}
                            onChange={setOrigin}
                            options={ORIGIN_OPTIONS}
                        />
                    </div>
                    <label className="relative w-[260px]">
                        <span className="sr-only">Search museums</span>
                        <IconSearch className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-content-muted" />
                        <input
                            type="search"
                            value={search}
                            onChange={(event) => {
                                setSearch(event.target.value);
                            }}
                            placeholder="Search museums"
                            className="w-full rounded-lg border border-line bg-surface py-2 pl-9 pr-3 text-[13.5px] text-content outline-none transition-colors placeholder:text-content-muted focus:border-brand focus:ring-2 focus:ring-accent-surface"
                        />
                    </label>
                </div>

                <MuseumsTable
                    query={museums}
                    rows={rows}
                    slotMissing={isSlotMissing}
                    search={linkSearch}
                />
            </Card>
        </ActivationPageFrame>
    );
}

function MuseumsTable({
    query,
    rows,
    slotMissing,
    search,
}: {
    query: ReturnType<typeof useActivationMuseums>;
    rows: MuseumRowView[];
    slotMissing: boolean;
    search: string;
}) {
    // Four different answers, and they send the operator to four different
    // places: choose a slot, turn the feature on somewhere, wait, or loosen
    // the filters.
    if (slotMissing) {
        return (
            <EmptyState
                title="No slot chosen"
                description="Pick a slot above to point at one of the ephemeral deployments."
            />
        );
    }

    if (query.isPending) return <SkeletonRows />;

    if (query.isError) {
        return (
            <EmptyState
                title="We couldn't load the museum list"
                description="Something went wrong on our side. Reload the page in a minute."
            />
        );
    }

    if (query.data.length === 0) {
        return (
            <EmptyState
                title="No museums have this feature enabled yet"
                description="Museums show up here as soon as Feature activation is turned on for their org."
            />
        );
    }

    if (rows.length === 0) {
        return (
            <EmptyState
                title="No museums match"
                description="Try another name, or clear the status and origin filters."
            />
        );
    }

    return (
        <div className="overflow-x-auto">
            <table className="w-full min-w-[920px] table-fixed border-collapse">
                <MuseumColumns />
                <thead>
                    <tr>
                        <Th>Museum</Th>
                        <Th>Status</Th>
                        <Th>Progress</Th>
                        <Th>First opened</Th>
                        <Th>Last activity</Th>
                        <Th>Missing</Th>
                    </tr>
                </thead>
                <tbody>
                    {rows.map((row) => (
                        <MuseumRow key={row.tenantId} row={row} search={search} />
                    ))}
                </tbody>
            </table>
        </div>
    );
}

/**
 * The columns' widths, measured off the design at its 1248px of content.
 *
 * Fixed, not left to the browser: with automatic layout the long "Missing"
 * lists took the room and pushed museum names onto two lines. The museum gets
 * the widest share, the four short columns an equal one, and "Missing" keeps
 * what is left, where wrapping is expected. The skeleton rows reuse the same
 * shares.
 */
function MuseumColumns() {
    return (
        <colgroup>
            <col className="w-[27%]" />
            <col className="w-[12%]" />
            <col className="w-[12%]" />
            <col className="w-[12%]" />
            <col className="w-[12%]" />
            <col />
        </colgroup>
    );
}

/**
 * A whole row opens the museum, as in the design.
 *
 * The name is still a real link: a `<tr>` with a click handler is not reachable
 * from the keyboard, and cannot be opened in a new tab. The link stops the
 * click from also reaching the row, which would navigate twice.
 */
function MuseumRow({ row, search }: { row: MuseumRowView; search: string }) {
    const navigate = useNavigate();
    const to = { pathname: `/activation-tracking/${encodeURIComponent(row.tenantId)}`, search };

    return (
        <tr
            onClick={() => {
                void navigate(to);
            }}
            className="cursor-pointer transition-colors hover:bg-surface-sunken"
        >
            <Td className="text-[14px] font-semibold text-content">
                <Link
                    to={to}
                    onClick={(event) => {
                        event.stopPropagation();
                    }}
                    className="hover:text-accent-text"
                >
                    {row.name}
                </Link>
            </Td>
            <Td className="whitespace-nowrap">
                <MuseumStatusBadge status={row.status} />
                {row.asOf && <Sub>{row.asOf}</Sub>}
            </Td>
            <Td>{row.progress && <Progress {...row.progress} />}</Td>
            <Td className="whitespace-nowrap text-[14px]">
                <div>{row.firstOpened}</div>
                {row.origin && <Sub>{row.origin}</Sub>}
            </Td>
            <Td className="whitespace-nowrap text-[14px]">{row.lastActivity}</Td>
            <Td className="text-[14px]">
                {row.missing && <div>{row.missing}</div>}
                {row.couldntCheck && (
                    <div className="mt-0.5 text-[12px] text-danger-muted">{row.couldntCheck}</div>
                )}
                {row.notSeen && (
                    <div className="mt-0.5 text-[12px] text-content-subtle">{row.notSeen}</div>
                )}
            </Td>
        </tr>
    );
}

/**
 * The loading state, as the design's: six placeholder rows laid out on the
 * table's own six columns — same widths, same cell padding — so the page does
 * not jump when the rows land.
 */
const SKELETON_ROWS = [0, 1, 2, 3, 4, 5] as const;

function SkeletonRows() {
    return (
        <div className="flex flex-col gap-[18px] py-[18px]" aria-busy="true">
            <span role="status" className="sr-only">
                Loading museums…
            </span>
            {SKELETON_ROWS.map((row) => (
                <div
                    key={row}
                    aria-hidden="true"
                    className="grid grid-cols-[27%_12%_12%_12%_12%_1fr] items-center"
                >
                    <Cell>
                        <Bone className="h-3.5 w-[70%] rounded" />
                    </Cell>
                    <Cell>
                        <Bone className="h-[22px] w-[84px] rounded-full" />
                    </Cell>
                    <Cell>
                        <Bone className="h-3.5 w-[60%] rounded" />
                    </Cell>
                    <Cell>
                        <Bone className="h-3.5 w-[70%] rounded" />
                    </Cell>
                    <Cell>
                        <Bone className="h-3.5 w-[60%] rounded" />
                    </Cell>
                    <Cell>
                        <Bone className="h-3.5 w-[80%] rounded" />
                    </Cell>
                </div>
            ))}
        </div>
    );
}

function Cell({ children }: { children: ReactNode }) {
    return <div className="min-w-0 px-[18px]">{children}</div>;
}

function Bone({ className }: { className: string }) {
    return <div className={cn('animate-pulse bg-surface-inset', className)} />;
}

function Sub({ children }: { children: ReactNode }) {
    return <div className="mt-1 text-[12px] text-content-muted">{children}</div>;
}

/** "X of N" over a thin bar. The kit has no progress bar; this is the design's. */
function Progress({ text, percent }: { text: string; percent: number }) {
    return (
        <>
            <div className="mb-1.5 text-[13px] font-semibold text-content">{text}</div>
            <div
                role="progressbar"
                aria-valuenow={Math.round(percent)}
                aria-valuemin={0}
                aria-valuemax={100}
                aria-label={text}
                className="h-1.5 w-full max-w-[110px] overflow-hidden rounded-full bg-surface-inset"
            >
                <div
                    className="h-full rounded-full bg-content-subtle"
                    style={{ width: `${String(percent)}%` }}
                />
            </div>
        </>
    );
}
